import { StateCreator } from "zustand";
import { supabase } from "../../supabase/supabaseClient";
import {
	JobWithShare,
	SupabaseRpcEnum,
	SupabaseTableEnum,
	SupabaseViewEnum,
} from "../../supabase/supabaseTypes";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { Logger } from "../../logger/Logger";
import { Tables } from "../../supabase/supabaseTypesImport";
import { TextMessage, MessageType, SharedFileMessageV2 } from "./message.types";
import { produce } from "immer";
import { StorageBucketsEnum } from "../../../dentlab/src/types/enums";
import { v4 as uuidv4 } from "uuid";
import { showNotification, showMessageNotification } from "../../../dentlab/src/store/Central/selectors";
import { useConnectStore } from "../../../dentlab/src/store/Connect";
import { FileInfo } from "../../../dentlab/src/components/channel/channel.component";
import { getMessagePreview } from "../../../dentlab/src/components/chat/message-preview";
import { ProfilesForChat } from "../../types/profile.types";
import { getAvatarUrl } from "../../utils/get-avatar-url";
import { navigationService } from "../../utils/navigation-service";

const getMessageRecipientOrgId = (channel: Channel, myOrgId: string) => {
	return channel.customer_org_id === myOrgId ? channel.vendor_org_id : channel.customer_org_id;
}

export enum ChannelType {
	JOB = "job",
	JOB_REQUEST = "job_request",
	GENERAL = "general",
	JOB_REQUEST_DENIED = "job_request_denied",
}

export type Channel = Tables<"message_channels">;

export interface MessageSlice {
	// As this store is used across apps, we can't get this information from central store
	userId: string | null;
	organizationId: string | null;
	initializeAuth: (userId: string, organizationId: string) => void;
	profilesForChat: Record<string, ProfilesForChat>;
	getProfilesForChat: () => Promise<void>;

	channel: Channel | null;

	// Allow changing the channel from the full channels or from a channel id (if full channel is not available)
	changeChannel: (channel: string) => Promise<void>;
	switchChannelForJobIfExists: (job: JobWithShare) => Promise<void>;

	messagesLoading: boolean;
	// For the overview of all chats, we store and subscribe to all messages
	messages: Record<string, Tables<"messages_with_actions">[]>;
	updateMessagesRealtime: (
		payload: RealtimePostgresChangesPayload<{ [key: string]: any }>
	) => void;
	updateActionRealtime: (
		payload: RealtimePostgresChangesPayload<{ [key: string]: any }>
	) => void;
	upsertAction: (messageId: string, actions: { [key: string]: string }) => Promise<void>;
	fetchMessages: (channel: Channel) => void;
	sendMessage: (firstMessage?: boolean, jobId?: number, vendorOrgId?: string, customerOrgId?: string) => Promise<void>;
	fetchLatestChannels: () => void;
	refreshSingleChannelFromNewMessage: (channelId: string) => Promise<Channel | null>;
	latestChannels: Tables<"message_channels">[];
	updateChannelReadState: (channelId: string) => Promise<void>;
	updateChannelReadStateRealtime: (payload: RealtimePostgresChangesPayload<{ [key: string]: any }>) => void;

	// Reply state
	// For each channel, we store the current message input globally
	// This allows drafts to be saved, even when switching between channels
	replyTexts: Record<string, string>;
	replyFiles: Record<string, FileInfo[]>;
	getReplyText: (channelId: string | null, jobId?: number) => string;
	setReplyText: (text: string, channelId: string | null, jobId?: number) => void;
	setReplyFiles: (files: FileInfo[], channelId: string | null, jobId?: number) => void;
	getReplyFiles: (channelId: string | null, jobId?: number) => FileInfo[];

	// Unread filter state
	showOnlyUnread: boolean;
	setShowOnlyUnread: (value: boolean) => void;

	// Job unread counts
	jobUnreadCounts: Record<number, number>;
	getJobUnreadCount: (jobId: number) => number;
	updateJobUnreadCounts: (channels: Channel[]) => void;

	// Total unread channels count
	getTotalUnreadChannelsCount: () => number;
}

const getPersistedShowOnlyUnread = (): boolean => {
	if (typeof window !== 'undefined') {
		const stored = localStorage.getItem('chat-show-only-unread');
		return stored === 'true';
	}
	return false;
};

const initialState: MessageSlice = {
	userId: null,
	organizationId: null,
	initializeAuth: () => { },
	profilesForChat: {},
	getProfilesForChat: () => Promise.resolve(),
	messagesLoading: false,
	messages: {},
	channel: null,
	replyTexts: {},
	replyFiles: {},
	latestChannels: [],
	setReplyText: () => { },
	getReplyText: () => "",
	changeChannel: () => Promise.resolve(),
	switchChannelForJobIfExists: () => Promise.resolve(),
	updateMessagesRealtime: () => { },
	updateActionRealtime: () => { },
	upsertAction: () => Promise.resolve(),
	fetchMessages: () => { },
	fetchLatestChannels: () => { },
	refreshSingleChannelFromNewMessage: () => Promise.resolve(null),
	sendMessage: () => Promise.resolve(),
	setReplyFiles: () => { },
	getReplyFiles: () => [],
	updateChannelReadState: () => Promise.resolve(),
	updateChannelReadStateRealtime: () => { },
	showOnlyUnread: getPersistedShowOnlyUnread(),
	setShowOnlyUnread: () => { },
	jobUnreadCounts: {},
	getJobUnreadCount: () => 0,
	updateJobUnreadCounts: () => { },
	getTotalUnreadChannelsCount: () => 0,
};

export const createMessageStore: StateCreator<
	MessageSlice,
	[],
	[],
	MessageSlice
> = (set, get) => ({
	...initialState,

	initializeAuth: (userId: string, organizationId: string) => {
		set({ userId, organizationId });
	},

	setShowOnlyUnread: (value: boolean) => {
		set({ showOnlyUnread: value });
		// Persist to localStorage for user preference
		localStorage.setItem('chat-show-only-unread', value.toString());
	},

	getJobUnreadCount: (jobId: number) => {
		return get().jobUnreadCounts[jobId] || 0;
	},

	updateJobUnreadCounts: (channels: Channel[]) => {
		const counts: Record<number, number> = {};

		channels.forEach(channel => {
			if (channel.channel_type === ChannelType.JOB && channel.job_id) {
				counts[channel.job_id] = channel.unread_count || 0;
			}
		});

		set({ jobUnreadCounts: { ...get().jobUnreadCounts, ...counts } });
	},

	getTotalUnreadChannelsCount: () => {
		const channels = get().latestChannels;
		return channels.filter(channel => (channel.unread_count || 0) > 0).length;
	},

	setReplyText: (text: string, channelId: string | null, jobId?: number) => {
		if (get().channel?.id != channelId) {
			Logger.error("Channel ID mismatch", {}, { channelId, channel: get().channel });
			return;
		}

		if (!channelId) {
			if (jobId) {
				set(
					produce((state) => {
						state.replyTexts[jobId] = text;
					})
				);
			}
			return;
		};

		set(
			produce((state) => {
				state.replyTexts[channelId] = text;
			})
		);
	},

	getReplyText: (channelId: string | null, jobId?: number) => {
		if (get().channel?.id != channelId) {
			Logger.error("Channel ID mismatch", {}, { channelId, channel: get().channel });
			return "";
		}

		if (!channelId) {
			if (jobId) {
				return get().replyTexts[jobId] || "";
			}
			return "";
		}

		return get().replyTexts[channelId] || "";
	},

	setReplyFiles: (files: FileInfo[], channelId: string | null, jobId?: number) => {
		if (get().channel?.id != channelId) {
			Logger.error("Channel ID mismatch", {}, { channelId, channel: get().channel });
			return;
		}

		if (!channelId) {
			if (jobId) {
				set(
					produce((state) => {
						state.replyFiles[jobId] = files;
					})
				);
			}
			return;
		};

		set(
			produce((state) => {
				state.replyFiles[channelId] = files;
			})
		);
	},

	getReplyFiles: (channelId: string | null, jobId?: number) => {
		if (get().channel?.id != channelId) {
			Logger.error("Channel ID mismatch", {}, { channelId, channel: get().channel });
			return [];
		}

		if (!channelId) {
			if (jobId) {
				return get().replyFiles[jobId] || [];
			}
			return [];
		}

		return get().replyFiles[channelId] || [];
	},

	changeChannel: async (channelId: string) => {
		Logger.log("Changing channel", {}, { channelId });
		const foundChannel = get().latestChannels.find(ch => ch.id === channelId);
		if (foundChannel) {
			set({ channel: foundChannel });
			get().fetchMessages(foundChannel);
		} else {
			const { data, error } = await supabase
				.from("message_channels")
				.select("*")
				.eq("id", channelId)
				.single();

			if (error) {
				Logger.error("Error fetching channel", {}, error);
				return;
			}

			set({ channel: data });
			get().fetchMessages(data);
		}
	},
	switchChannelForJobIfExists: async (job: JobWithShare) => {
		// If job has no channel_id, clear the current channel
		if (!job.channel_id) {
			set({ channel: null, messages: {} });
			return;
		}

		await get().changeChannel(job.channel_id);
	},
	updateMessagesRealtime: async (payload) => {
		if (payload.eventType !== "INSERT" && payload.eventType !== "UPDATE") {
			Logger.warn("Event type not Insert or Update for updating message");
			return;
		}

		Logger.log("Update messages realtime", {}, payload);

		const newMessage = payload.new as Tables<"messages_with_actions">;
		const channelId = newMessage.channel_id;

		if (!channelId) {
			Logger.error("Message has no channel_id");
			return;
		}


		// If a new message was inserted, we need to update the channels to reflect this in
		// - the preview and time of the latest message
		// - depending on situation, the unread state (including unread count)
		if (payload.eventType === "INSERT") {
			const newMessageSentByMe = newMessage.created_by === get().userId;
			// If the message was sent by me, the database updates the unread state because
			// we can always assme I have read until the message I have sent, so all we do is get up to date with the db
			if (newMessageSentByMe) {
				await get().refreshSingleChannelFromNewMessage(channelId);
			} else {
				// If the message was not sent by me, how we should deal with it depends on whether we read the message as it arrives
				// or if we are not currently looking at the channel
				const channelIsOpenAndFocused = get().channel?.id === channelId && document.hasFocus();

				// Some additonal preparation
				const channelIndex = get().latestChannels.findIndex(ch => ch.id === channelId);
				const affectedChannel = channelIndex !== -1 ? get().latestChannels[channelIndex] : null;

				if (channelIsOpenAndFocused) {
					// If the channel is open and focused, we need to let the database know that we have read the message
					get().updateChannelReadState(channelId);

					// Additionally, we update the channel preview and move the channel to the top of the list
					// Because the channel is open and focused, we assume that we have found it in latestChannels
					// If not, we raise an error
					if (affectedChannel) {
						set(produce((state) => {
							state.latestChannels.splice(channelIndex, 1);
							state.latestChannels.unshift({
								...affectedChannel,
								last_message_at: newMessage.created_at,
								last_message_content: newMessage.content,
								last_message_created_by: newMessage.created_by,
								unread_count: 0,
							});
						}));
					} else {
						Logger.error("Channel not found in latestChannels");
						showNotification({
							message: "Fehler beim Laden der Nachricht (2).",
							type: "error",
						});
					}
				} else {
					// If the channel is not open and focused, we need to both update the preview and the unread count
					let channelForNotification: Channel | null = null;

					if (affectedChannel) {
						const newUnreadCount = (affectedChannel.unread_count || 0) + 1;
						set(produce((state) => {
							state.latestChannels.splice(channelIndex, 1);
							state.latestChannels.unshift({
								...affectedChannel,
								last_message_at: newMessage.created_at,
								last_message_content: newMessage.content,
								last_message_created_by: newMessage.created_by,
								unread_count: newUnreadCount,
							});
							if (affectedChannel.channel_type === ChannelType.JOB && affectedChannel.job_id) {
								state.jobUnreadCounts[affectedChannel.job_id] = newUnreadCount;
							}
						}));
						channelForNotification = affectedChannel;
					} else {
						// If we don't find the channel, we fall back to refreshing it entirely which also recalculates the unread count
						// Refresh single channel also updated job unread counts
						channelForNotification = await get().refreshSingleChannelFromNewMessage(channelId);
					}

					// Show notification for new message
					if (channelForNotification) {
						const organizationId = get().organizationId;
						if (organizationId) {
							const { brandsByOrgId } = useConnectStore.getState();

							// Get channel title
							let channelTitle = "";
							if (channelForNotification.channel_type === ChannelType.JOB && channelForNotification.job_title) {
								channelTitle = channelForNotification.job_title;
							} else {
								const otherOrgId = channelForNotification.customer_org_id === organizationId
									? channelForNotification.vendor_org_id
									: channelForNotification.customer_org_id;
								channelTitle = brandsByOrgId[otherOrgId || ""]?.name || "Neuer Chat";
							}

							const messagePreview = getMessagePreview(newMessage.content as string, false);

							// Get sender profile from profiles
							const senderProfile = get().profilesForChat[newMessage.created_by || ""];
							const senderName = senderProfile
								? `${senderProfile.first_name} ${senderProfile.last_name}`.trim()
								: "Unbekannter Nutzer";

							showMessageNotification({
								channelName: channelTitle,
								senderName: senderName,
								senderAvatar: senderProfile?.avatar_url,
								message: messagePreview,
								duration: 8000,
								onAction: () => {
									navigationService.navigateTo(`/chats/${channelId}`);
								}
							});
						}
					}
				}
			}
		}

		// Update messages for the channel
		set(
			produce((state) => {
				if (payload.eventType === "INSERT") {
					state.messages[channelId] = [
						...(state.messages[channelId] || []),
						newMessage,
					];
				} else if (payload.eventType === "UPDATE") {
					// Find and update the existing message
					if (state.messages[channelId]) {
						state.messages[channelId] = state.messages[channelId].map((message: Tables<"messages_with_actions">) =>
							message.id === newMessage.id ? newMessage : message
						);
					}
				}
			})
		);

		// If the message is a job request, we might need to refresh the vendors and profiles
		// because it could be the first time, the other organization is reaching out
		const messageType = (newMessage.content as any)?.type;
		const isJobRequest = messageType === MessageType.JOB_REQUEST_V1 || messageType === MessageType.JOB_REQUEST_CUSTOM_V1;
		if (isJobRequest) {
			const profilesForChat = get().profilesForChat;

			if (!profilesForChat[newMessage.created_by ?? ""]) {
				await get().getProfilesForChat();
			}

			const organizationId = get().organizationId;
			if (!organizationId) {
				Logger.error("No organization ID found. Call initializeAuth first.");
				showNotification({
					message: "Fehler beim Laden der Verbindungen (1).",
					type: "error",
				});
				return;
			}

			// TODO: This might have unnecessary fetches
			const { fetchConnectedVendors, fetchBrands } = useConnectStore.getState();

			await fetchConnectedVendors(organizationId);
			await fetchBrands();
		}
	},

	updateActionRealtime: (payload: RealtimePostgresChangesPayload<{ [key: string]: any }>) => {
		if (payload.eventType !== "UPDATE" && payload.eventType !== "INSERT") {
			Logger.warn("Event type not Update or Insert for updating action");
			return;
		}

		Logger.log("Update action", {}, payload);

		// Find the message across all channels
		const allMessages = Object.values(get().messages).flat();
		const message = allMessages.find((message: Tables<"messages_with_actions">) => message.id === payload.new.message_id);

		if (!message || !message.channel_id) {
			Logger.warn("Message not found or has no channel_id");
			return;
		}

		const channelId = message.channel_id;

		set(
			produce((state) => {
				if (state.messages[channelId]) {
					state.messages[channelId] = state.messages[channelId].map((msg: Tables<"messages_with_actions">) =>
						msg.id === payload.new.message_id ? { ...msg, actions: payload.new.actions } : msg
					);
				}
			})
		);
	},

	// Note that the read state is not updated here but instead
	// on component mount of the channel that uses the messages
	fetchMessages: async (channel) => {
		Logger.log("Fetching messages for channel", {}, { channel });
		set({ messagesLoading: true });
		if (!channel || !channel.id) {
			Logger.error("Asked to run fetchMessages on a channel that doesn't exist", {}, { channel });
			return;
		};

		const { data, error } = await supabase
			.from(SupabaseViewEnum.MESSAGES_WITH_ACTIONS)
			.select()
			.eq("channel_id", channel.id)
			.order("created_at", { ascending: true });

		if (error) {
			Logger.error(error);
			set({ messagesLoading: false });
			return;
		}

		Logger.log("Messages", {}, data);

		if (data) {
			set(produce((state) => {
				state.messages[channel.id as string] = data;
			}));
		}

		set({
			messagesLoading: false,
		});
	},

	fetchLatestChannels: async () => {
		const organizationId = get().organizationId;
		if (!organizationId) {
			Logger.error("No organization ID found. Call initializeAuth first.");
			return;
		}

		const CHANNEL_LIMIT= 200;

		const { data, error } = await supabase
			.from("message_channels")
			.select("*")
			.or(`vendor_org_id.eq.${organizationId},customer_org_id.eq.${organizationId}`)
			.order("last_message_at", { ascending: false })
			.limit(CHANNEL_LIMIT);

		if (error) {
			Logger.error(error);
			return;
		}

		if (!data) {
			Logger.error("No data returned from fetchLatestChannels");
			return;
		}

		const channels = data || [];

		Logger.log("fetchLatestChannels - channels", {}, { channels: channels?.length });

		// Also fetch any unread or general channels older than the oldest message from the 200 latest channels
		// The oldest message is the last message in the data array

		// If we have less than the limit, we already have all the channels
		if (data.length === CHANNEL_LIMIT) {
			Logger.log("fetchLatestChannels - fetching unread and general channels", {}, { channels: channels?.length });
			const oldestMessage = data[data.length - 1];
			if (oldestMessage) {
				Logger.log("fetchLatestChannels - fetching unread channels", {}, { oldestMessage: oldestMessage.last_message_at });
				const { data: unreadChannels, error: unreadChannelsError } = await supabase
					.from("message_channels")
					.select("*")
					.gt("unread_count", 0)
					.neq("channel_type", ChannelType.GENERAL)
					.lt("last_message_at", oldestMessage.last_message_at)
					.limit(1000);

				if (unreadChannelsError) {
					Logger.error("fetchLatestChannels - Error fetching unread channels", {}, unreadChannelsError);
				}

				if (unreadChannels) {
					channels.push(...unreadChannels);
				}

				const { data: generalChannels, error: generalChannelsError } = await supabase
					.from("message_channels")
					.select("*")
					.eq("channel_type", ChannelType.GENERAL)
					.lt("last_message_at", oldestMessage.last_message_at)
					.limit(1000);

				if (generalChannelsError) {
					Logger.error("fetchLatestChannels - Error fetching general channels", {}, generalChannelsError);
				}

				if (generalChannels) {
					channels.push(...generalChannels);
				}
			}
		}

		set({ latestChannels: channels });

		get().updateJobUnreadCounts(channels);
	},

	refreshSingleChannelFromNewMessage: async (channelId: string) => {
		const organizationId = get().organizationId;
		if (!organizationId) {
			Logger.error("No organization ID found. Call initializeAuth first.");
			return null;
		}

		const { data: channel, error } = await supabase
			.from("message_channels")
			.select("*")
			.eq("id", channelId)
			.single();

		if (error) {
			Logger.error("Error fetching single channel", {}, error);
			return null;
		}

		if (channel) {
			set(
				produce((state) => {
					// Remove the channel from the list if it's already there
					const channelIndex = state.latestChannels.findIndex((ch: Channel) => ch.id === channel.id);
					if (channelIndex !== -1) {
						state.latestChannels.splice(channelIndex, 1);
					}

					// Add the channel to the top of the list
					state.latestChannels.unshift(channel);

					// Update job unread counts if it's a job channel
					if (channel.channel_type === ChannelType.JOB && channel.job_id) {
						// Initialize unread count if not already set
						if (typeof channel.unread_count === 'number') {
							state.jobUnreadCounts[channel.job_id] = channel.unread_count;
						}
					}
				})
			);
		}

		return channel;
	},

	sendMessage: async (firstMessage?: boolean, jobId?: number, vendorOrgId?: string, customerOrgId?: string) => {
		const organizationId = get().organizationId;

		if (!organizationId) {
			Logger.error("No organization ID found. Call initializeAuth first.");
			showNotification({
				message: "Fehler beim Senden der Nachricht. Keine Organisation gefunden.",
				type: "error",
			});
			return;
		}

		let channelId: string | null = null;
		let channelType: ChannelType | null = null;
		let channelJobId: number | null = null;
		let recipientOrgId: string | null = null;


		let channel: Channel | null = get().channel;

		// We need to get the text and files immediately because they might be stored in "empty"
		const text = get().getReplyText(channel?.id || null, jobId);
		const files = get().getReplyFiles(channel?.id || null, jobId);

		if (firstMessage) {
			if (!jobId || !vendorOrgId || !customerOrgId) {
				Logger.error("No job ID, vendor org ID, or customer org ID found");
				showNotification({
					message: "Fehler beim Senden der Nachricht. Keine Auftrags-ID, Verkäufer-ID, oder Kunden-ID gefunden.",
					type: "error",
				});
				return;
			}

			const { data: channelResult, error } = await supabase
				.rpc('get_or_create_job_channel', {
					p_job_id: jobId,
					p_vendor_org_id: vendorOrgId,
					p_customer_org_id: customerOrgId,
					p_return_additional_data: true,
				});

			if (error) {
				Logger.error("Error creating channel", {}, error);
				showNotification({
					message: "Fehler beim Erstellen des Kanals",
					type: "error",
				});
				return;
			}

			if (!channelResult || !(channelResult as any).success) {
				Logger.error("Failed to create channel", {}, channelResult);
				showNotification({
					message: "Fehler beim Erstellen des Kanals",
					type: "error",
				});
				return;
			}

			Logger.log("Channel created", {}, channelResult);
			channel = (channelResult as any).channel as Channel;
			channelId = channel.id;
			channelType = channel.channel_type as ChannelType;
			channelJobId = channel.job_id;
			recipientOrgId = channel.customer_org_id;
			if (!channelId) {
				Logger.error("No channel ID found");
				showNotification({
					message: "Fehler beim Senden der Nachricht. Kein Kanal gefunden.",
					type: "error",
				});
				return;
			}
		} else {
			if (!channel) {
				Logger.error("No channel found in state");
				showNotification({
					message: "Fehler beim Senden der Nachricht. Kein Chat ausgewählt.",
					type: "error",
				});
				return;
			}
			channelId = channel.id;
			channelType = (channel.channel_type as ChannelType) || null;
			channelJobId = (channel.job_id as number) || null;
			recipientOrgId = getMessageRecipientOrgId(channel, organizationId);
		}

		if (!channelType || !channelId || !recipientOrgId) {
			Logger.error("No channel found", {}, { channelType, channelId, recipientOrgId });
			showNotification({
				message:
					"Fehler beim Senden der Nachricht. Fehlende Informationen.",
				type: "error",
			});
			return;
		}

		const cleanedText = text
			.replace(/<p><br><\/p>(\s*<p><br><\/p>)+/g, "<p><br></p>") // reduce multiple <p><br></p> to one
			.replace(/^(\s*<p><br><\/p>)+/, "") // remove empty paragraphs at the beginning
			.replace(/(\s*<p><br><\/p>)+$/, ""); // remove empty paragraphs at the end

		const isOnlyHtmlTags =
			cleanedText.replace(/<[^>]*>/g, "").trim().length === 0 &&
			files.length === 0;

		if (isOnlyHtmlTags) {
			set(
				produce((state) => {
					state.replyTexts[channelId] = "";
					state.replyFiles[channelId] = [];
					if (jobId) {
						state.replyTexts[jobId] = "";
						state.replyFiles[jobId] = [];
					}
				})
			);
			showNotification({
				message: "Die Nachricht ist leer.",
				type: "info",
			});
			return;
		}

		const fileIds = files.map(async (file) => {
			const filePath = `${uuidv4()}/${file.name}`;

			// Convert the file data to a format that can be uploaded
			// The file.data is a base64 string, we need to convert it to a Blob
			let fileData: Blob;
			if (
				typeof file.data === "string" &&
				file.data.startsWith("data:")
			) {
				// It's a data URL, extract the base64 part and convert to Blob
				const base64Data = file.data.split(",")[1];
				const byteCharacters = atob(base64Data);
				const byteArrays = [];

				for (
					let offset = 0;
					offset < byteCharacters.length;
					offset += 512
				) {
					const slice = byteCharacters.slice(offset, offset + 512);
					const byteNumbers = new Array(slice.length);

					for (let i = 0; i < slice.length; i++) {
						byteNumbers[i] = slice.charCodeAt(i);
					}

					const byteArray = new Uint8Array(byteNumbers);
					byteArrays.push(byteArray);
				}

				fileData = new Blob(byteArrays, { type: file.type });
			} else if (file.data instanceof ArrayBuffer) {
				fileData = new Blob([file.data], { type: file.type });
			} else {
				Logger.error("Unsupported file data format");
				showNotification({
					message: "Dateityp nicht unterstützt",
					type: "error",
				});
				return null;
			}

			// Upload the file to storage
			const { error: uploadError } = await supabase.storage
				.from(StorageBucketsEnum.V1)
				.upload(filePath, fileData);

			if (uploadError) {
				Logger.error("Error uploading file", {}, uploadError);
				showNotification({
					message: "Fehler beim Hochladen der Datei" + file.name,
					type: "error",
				});
				return null;
			}

			const jobId = channelType === ChannelType.JOB ? channelJobId : null;

			if (!recipientOrgId) {
				Logger.error("No recipient organization ID found");
				return null;
			}

			// TODO: Maybe this RPC should just take channel_id instead of recipient and job; or just job
			const { data, error: rpcError } = await supabase.rpc(
				SupabaseRpcEnum.UPLOAD_AND_SHARE_JOB_FILE,
				{
					p_file_path_name: filePath,
					p_file_name: file.name,
					p_bucket_name: StorageBucketsEnum.V1,
					p_message_text: "",
					p_recipient_org_id: recipientOrgId,
					p_job_id: jobId ?? null as any, // p_job_id is allowed to be null, but TypeScript doesn't know that
				}
			);

			if (
				rpcError ||
				!(data && typeof data === "object" && "file_id" in data)
			) {
				Logger.error("Error sharing file", {}, rpcError);
				return null;
			}

			return data;
		});

		// Wait for all file uploads to complete
		const uploadedFiles = await Promise.all(fileIds);
		const failedUploads = uploadedFiles.filter(
			(file) => !file || !file.file_id
		);

		if (failedUploads.length > 0) {
			Logger.error("Error uploading files", {}, failedUploads);
			showNotification({
				message:
					"Fehler beim Hochladen folgender Dateien: " +
					failedUploads
						.map((file) => file && file.file_name)
						.join(", "),
				type: "error",
			});
			return;
		}

		const uploadedFileIds = uploadedFiles.map(
			(file) => file && file.file_id
		) as string[];

		let messageContent: TextMessage | SharedFileMessageV2;

		if (uploadedFileIds.length > 0) {
			const content: SharedFileMessageV2 = {
				type: MessageType.SHARED_FILE_V2,
				file_ids: uploadedFileIds,
				text: cleanedText,
			};
			messageContent = content;
		} else {
			const content: TextMessage = {
				type: MessageType.TEXT,
				text: cleanedText,
			};
			messageContent = content;
		}

		if (!messageContent) {
			Logger.error("No message content found");
			showNotification({
				message: "Fehler beim Senden der Nachricht. Keine Nachrichteninhalte gefunden.",
				type: "error",
			});
			return;
		}


		// Note that created_by is set automatically on the database
		const { data: messageData, error } = await supabase
			.from("messages")
			.insert({
				content: JSON.stringify(messageContent),
				channel_id: channelId,
			})
			.select()
			.single();

		if (error) {
			Logger.error("Error sending text message", {}, error);
			showNotification({
				message: "Fehler beim Senden der Nachricht an die Datenbank.",
				type: "error",
			});
		}
		Logger.log("Message sent", {}, messageData);

		// We don't set state here, because the realtime subscription will update the messages

		set(
			produce((state) => {
				if (firstMessage) {
					state.channel = channel;
					state.latestChannels.unshift(channel);
				}
				state.replyTexts[channelId] = "";
				state.replyFiles[channelId] = [];
				if (jobId) {
					state.replyTexts[jobId] = "";
					state.replyFiles[jobId] = [];
				}
			})
		);
	},

	upsertAction: async (messageId: string, action: { [key: string]: string }) => {
		// First get the existing actions
		const { data, error: fetchError } = await supabase
			.from(SupabaseTableEnum.MESSAGE_ACTIONS)
			.select('actions')
			.eq('message_id', messageId);

		if (fetchError) {
			Logger.error("Error fetching existing actions", {}, fetchError);
			return;
		}

		const existingData = data?.[0];

		// Merge the existing actions with the new action
		const existingActions = (existingData?.actions || {}) as { [key: string]: string };
		const mergedActions = { ...existingActions, ...action };

		// Update with the merged actions
		const { error } = await supabase
			.from(SupabaseTableEnum.MESSAGE_ACTIONS)
			.upsert({
				message_id: messageId,
				actions: mergedActions
			}, { onConflict: "message_id" });

		if (error) {
			Logger.error("Error upserting action", {}, error);
		}
	},
	updateChannelReadState: async (channelId: string) => {
		const userId = get().userId;
		if (!userId) {
			Logger.error("No user ID found. Call initializeAuth first.");
			return;
		}

		try {
			const { error } = await supabase
				.from("user_channel_read_state")
				.upsert({
					user_id: userId,
					channel_id: channelId,
					last_read_at: new Date().toISOString()
				}, { onConflict: "user_id,channel_id" });

			if (error) {
				Logger.error("Error updating channel read state", {}, error);
				return;
			}

			// Optimistically update the local state to clear unread count
			set(
				produce((state) => {
					// Update in latestChannels
					const channelIndex = state.latestChannels.findIndex(
						(ch: Channel) => ch.id === channelId
					);
					if (channelIndex !== -1) {
						state.latestChannels[channelIndex].unread_count = 0;
						state.latestChannels[channelIndex].last_read_at = new Date().toISOString();

						// Clear job unread count if this is a job channel
						const channel = state.latestChannels[channelIndex];
						if (channel && channel.channel_type === ChannelType.JOB && channel.job_id) {
							state.jobUnreadCounts[channel.job_id] = 0;
						}
					}
				})
			);
		} catch (error) {
			Logger.error("Error in updateChannelReadState", {}, error);
		}
	},

	updateChannelReadStateRealtime: (payload) => {
		if (payload.eventType !== "INSERT" && payload.eventType !== "UPDATE") {
			return;
		}

		const readState = payload.new as { user_id: string; channel_id: string; last_read_at: string };
		const userId = get().userId;

		// Only process if this is for the current user
		if (!userId || readState.user_id !== userId) {
			return;
		}

		set(
			produce((state) => {
				// Find and update the channel in all lists
				const updateChannel = (channels: Channel[]) => {
					const channelIndex = channels.findIndex((ch: Channel) => ch.id === readState.channel_id);
					if (channelIndex !== -1) {
						channels[channelIndex].last_read_at = readState.last_read_at;
						channels[channelIndex].unread_count = 0;

						// Update job unread count if this is a job channel
						const channel = channels[channelIndex];
						if (channel.channel_type === ChannelType.JOB && channel.job_id) {
							state.jobUnreadCounts[channel.job_id] = 0;
						}
						return true;
					}
					return false;
				};

				updateChannel(state.latestChannels);
			})
		);
	},
	getProfilesForChat: async () => {
		// TODO: Here we rely on row level security to get the correct profiles
		// This is not a good pattern. 
		const { data, error } = await supabase
			.from("profiles")
			.select("*")

		if (error) {
			Logger.error(
				error,
				{},
				"Error fetching connected profiles for lab"
			);
			return;
		}

		const connectedProfiles = data.map(async (row) => {
			return {
				profile_id: row.id,

				first_name: row.first_name ?? "",
				last_name: row.last_name ?? "",
				email: row.email ?? "",
				avatar_url: row.avatar_url ?
					await getAvatarUrl(row.organization_id ?? "", row.avatar_url) : "",
				organization_id: row.organization_id,
			};
		});

		const profiles = await Promise.all(connectedProfiles);
		const profilesLookup = profiles.reduce((acc, profile) => {
			acc[profile.profile_id] = profile;
			return acc;
		}, {} as Record<string, ProfilesForChat>);

		set({
			profilesForChat: profilesLookup,
		});
	},
});
